import { useMemo } from 'react';

import { useAuthentication } from '@/hooks/queries/authentication/useAuthentication';
import { useIsMobile } from '@/hooks/useIsMobile';

import { useLocalNavigationMenu } from './useLocalNavigationMenu';
import { type LocalNavigationItem } from '../schemas/localNavigation.schema';
import {
  type LocalNavigationMenu,
  type LocalNavigationMenuGroup,
  type LocalNavigationMenuRawItem,
} from '../schemas/localNavigationMenu.schema';

type UtilityAction = 'open-ti-support' | 'logout';

export type LocalNavigationLink = {
  id?: number;
  text: string;
  title?: string;
  icon?: LocalNavigationItem['icon'];
  url?: string;
  targetEnvironment?: string;
  fragment?: string;
  queryParams?: Record<string, string>;
  queryParamsHandling?: 'merge' | 'preserve' | '';
  action?: UtilityAction;
};

type LocalNavigationAccordionItem = {
  id: number;
  title: string;
  text: string;
  icon?: LocalNavigationItem['icon'];
  description?: string;
  url?: string;
  targetEnvironment?: string;
  links: LocalNavigationLink[];
};

type LocalNavigationDirectLinkItem = {
  id: number;
  title: string;
  text: string;
  icon: LocalNavigationItem['icon'];
  description?: string;
  url?: string;
  targetEnvironment?: string;
  fragment?: string;
  queryParams?: Record<string, string>;
  queryParamsHandling?: 'merge' | 'preserve' | '';
  action?: UtilityAction;
};

export type LocalNavigationMenuItem =
  ({ type: 'submenu' } & LocalNavigationAccordionItem) | ({ type: 'link' } & LocalNavigationDirectLinkItem);

export type LocalNavigationMenuSection = {
  title: string;
  items: LocalNavigationMenuItem[];
  isUtility?: boolean;
};

function normalizeIcon(input: unknown): LocalNavigationItem['icon'] | undefined {
  if (typeof input === 'string' && input.trim()) {
    return input.trim() as LocalNavigationItem['icon'];
  }

  if (Array.isArray(input)) {
    for (const entry of input) {
      const normalized = normalizeIcon(entry);
      if (normalized) return normalized;
    }
  }

  return undefined;
}

function isAdministrativeProfile(profile: string): boolean {
  return ['admin', 'super admin', 'super-admin', 'superadmin', 'super_admin'].includes(profile.trim().toLowerCase());
}

function extractUrlFromUnknown(input: unknown): string {
  if (typeof input === 'string') {
    return input.trim();
  }

  if (Array.isArray(input)) {
    for (const entry of input) {
      const path = extractUrlFromUnknown(entry);
      if (path) return path;
    }

    return '';
  }

  if (typeof input === 'object' && input !== null) {
    const record = input as Record<string, unknown>;
    const preferredKeys = ['url', 'href', 'path', 'link', 'routerLink', 'to', 'slug'];

    for (const key of preferredKeys) {
      if (!(key in record)) continue;
      const path = extractUrlFromUnknown(record[key]);
      if (path) return path;
    }
  }

  return '';
}

function parseLink(input: unknown, fallbackText: string, index: number): LocalNavigationLink | null {
  const url = extractUrlFromUnknown(input);

  let text = '';
  let title = '';
  let icon: LocalNavigationItem['icon'] | undefined;
  let targetEnvironment: string | undefined;
  let fragment: string | undefined;
  let queryParams: Record<string, string> | undefined;
  let queryParamsHandling: 'merge' | 'preserve' | '' | undefined;
  let action: UtilityAction | undefined;

  if (typeof input === 'object' && input !== null) {
    const record = input as Record<string, unknown>;
    const preferredKeys = ['text', 'title', 'description', 'label', 'name'];

    for (const key of preferredKeys) {
      const value = record[key];
      if (typeof value === 'string' && value.trim()) {
        text = value.trim();
        break;
      }
    }

    if (typeof record.title === 'string' && record.title.trim()) {
      title = record.title.trim();
    }

    icon = normalizeIcon(record.icon);

    if (typeof record.targetEnvironment === 'string') {
      targetEnvironment = record.targetEnvironment;
    }

    if (typeof record.fragment === 'string' && record.fragment.trim()) {
      fragment = record.fragment.trim();
    }

    if (typeof record.queryParamsHandling === 'string') {
      queryParamsHandling = record.queryParamsHandling as 'merge' | 'preserve' | '';
    }

    if (typeof record.action === 'string') {
      action = record.action as UtilityAction;
    }

    if (typeof record.queryParams === 'object' && record.queryParams !== null && !Array.isArray(record.queryParams)) {
      queryParams = Object.entries(record.queryParams).reduce<Record<string, string>>((acc, [key, value]) => {
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          acc[key] = String(value);
        }

        return acc;
      }, {});
    }
  }

  if (!url && !action) return null;

  return {
    id:
      typeof input === 'object' && input !== null && typeof (input as Record<string, unknown>).id === 'number'
        ? ((input as Record<string, unknown>).id as number)
        : undefined,
    text: text || fallbackText || `Link ${index + 1}`,
    title: title || text || fallbackText || `Link ${index + 1}`,
    icon,
    url: url || undefined,
    targetEnvironment,
    fragment,
    queryParams,
    queryParamsHandling,
    action,
  };
}

function resolveMenuIcon(path: string): LocalNavigationItem['icon'] {
  if (path === '/') return 'home';
  if (path.includes('resultado') || path.includes('evolucao') || path.includes('dash')) return 'chart-pie';
  if (path.includes('coordenador') || path.includes('usuario') || path.includes('user')) return 'users';
  if (path.includes('tema')) return 'book';
  if (path.includes('tarefa')) return 'check-circle';
  return 'grid-squares';
}

function mapItemLinks(item: LocalNavigationMenuRawItem): LocalNavigationLink[] {
  return item.links.reduce<LocalNavigationLink[]>((acc, link, index) => {
    const parsed = parseLink(link, item.text, index);
    if (!parsed) return acc;

    if (!parsed.targetEnvironment && item.targetEnvironment) {
      parsed.targetEnvironment = item.targetEnvironment;
    }

    acc.push(parsed);
    return acc;
  }, []);
}

function mapMenuToLocalNavigationSections(
  menu: LocalNavigationMenu,
  forceSubmenu: boolean
): LocalNavigationMenuSection[] {
  return menu
    .map((group) => {
      const sectionItems: LocalNavigationMenuItem[] = [];

      group.items.forEach((item) => {
        const links = mapItemLinks(item).filter((link) => !!link.url || !!link.action);
        const directUrl = extractUrlFromUnknown(item.url);
        const title = item.description?.trim() || item.text || group.title;
        const text = item.text?.trim() || title;
        const firstLink = links[0]?.url ?? directUrl;
        const icon = normalizeIcon(item.icon) ?? resolveMenuIcon(firstLink || '');

        if (forceSubmenu) {
          const submenuLinks = links.length
            ? links
            : directUrl
              ? [{ text, title, url: directUrl, targetEnvironment: item.targetEnvironment }]
              : [];

          if (!submenuLinks.length) return;

          sectionItems.push({
            type: 'submenu',
            id: item.id,
            title,
            text,
            icon,
            description: item.description,
            url: directUrl || undefined,
            targetEnvironment: item.targetEnvironment,
            links: submenuLinks,
          });
          return;
        }

        if (links.length) {
          sectionItems.push({
            type: 'submenu',
            id: item.id,
            title,
            text,
            icon,
            description: item.description,
            url: directUrl || undefined,
            targetEnvironment: item.targetEnvironment,
            links,
          });
          return;
        }

        if (!directUrl) return;

        sectionItems.push({
          type: 'link',
          id: item.id,
          title,
          text,
          icon,
          url: directUrl,
          description: item.description,
          targetEnvironment: item.targetEnvironment,
        });
      });

      return {
        title: group.title,
        items: sectionItems,
      };
    })
    .filter((section) => section.items.length > 0);
}

function appendMobileUtilityGroups(
  sections: LocalNavigationMenuSection[],
  profile: string
): LocalNavigationMenuSection[] {
  const utilityItems: LocalNavigationMenuItem[] = [];
  const normalizedProfile = profile.trim().toLowerCase();

  const helpLinks: LocalNavigationLink[] = [];

  if (isAdministrativeProfile(normalizedProfile)) {
    helpLinks.push({
      id: -11,
      text: 'Suporte TI',
      action: 'open-ti-support',
    });
  }

  const profileLinks: LocalNavigationLink[] = [
    {
      id: -21,
      text: 'Meus dados',
      url: '/cadastro/perfil',
      targetEnvironment: 'ANGULAR',
    },
  ];

  helpLinks.push({
    id: -13,
    text: 'Fale com a gente',
    url: '/usuario/contato',
    targetEnvironment: 'ANGULAR',
    fragment: 'inicio',
  });

  utilityItems.push({
    type: 'submenu',
    id: -2,
    title: 'Perfil',
    text: 'Meu Perfil',
    description: 'Atalhos do perfil',
    icon: 'user-female-circle',
    links: profileLinks,
  });

  if (helpLinks.length) {
    utilityItems.push({
      type: 'submenu',
      id: -1,
      title: 'Ajuda',
      text: 'Ajuda',
      description: 'Atalhos de ajuda',
      icon: 'question-circle',
      links: helpLinks,
    });
  }

  if (normalizedProfile === 'aluno') {
    profileLinks.push({
      id: -22,
      text: 'Avaliação diagnóstica',
      url: '/avaliacao/diagnostica',
      targetEnvironment: 'ANGULAR',
    });
  }

  profileLinks.push({
    id: -23,
    text: 'Sair',
    action: 'logout',
  });

  return utilityItems.length ? [{ title: '', items: utilityItems, isUtility: true }, ...sections] : sections;
}

type UseLocalNavigationMenuSectionsReturn = {
  localNavigationSections: LocalNavigationMenuSection[];
  isLoadingMenu: boolean;
  hasMenuError: boolean;
  isMobile: boolean;
};

export function useLocalNavigationMenuSections(items?: LocalNavigationItem[]): UseLocalNavigationMenuSectionsReturn {
  const isMobile = useIsMobile();
  const { data: userData } = useAuthentication();
  const { data: menuData, isLoading: isLoadingMenu, isError: hasMenuError } = useLocalNavigationMenu();
  const menu = useMemo<LocalNavigationMenu>(() => menuData ?? ([] as LocalNavigationMenuGroup[]), [menuData]);
  const profile = userData?.perfil?.toLowerCase().trim() ?? '';

  const isAdminProfile = useMemo(() => isAdministrativeProfile(profile), [profile]);

  const localNavigationSections = useMemo(() => {
    if (menu.length) {
      const mappedSections = mapMenuToLocalNavigationSections(menu, isAdminProfile);
      return isMobile ? appendMobileUtilityGroups(mappedSections, profile) : mappedSections;
    }

    if (!items?.length) return [];

    return [
      {
        title: 'Acesso rápido',
        items: [
          {
            type: 'submenu',
            id: -1,
            title: 'Acesso rápido',
            text: 'Acesso rápido',
            icon: 'grid-squares',
            links: items.map((item) => ({ text: item.label, url: item.to, targetEnvironment: 'REACT' })),
          },
        ],
      } satisfies LocalNavigationMenuSection,
    ];
  }, [isAdminProfile, isMobile, items, menu, profile]);

  return { localNavigationSections, isLoadingMenu, hasMenuError, isMobile };
}
